import { Box, Button, Chip, Dialog, DialogContent, DialogTitle, MenuItem, Paper, Select, Typography } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import Header from '../../../components/navigation/Header';
import { makeStyles } from "@mui/styles";
import { IconButton } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import KenTable from '../../../components/KenTable';
import { SelectChangeEvent } from '@mui/material/Select';
import NetworkAnalysis from '../../networkAnalysis';
import CloseIcon from '@mui/icons-material/Close';
import { getMarksBreakdown } from '../../../utils/apiService';

const useStyles = makeStyles(() => ({
    header: {
        fontSize: '18px',
        fontWeight: '500',
    },
    container: {
        borderRadius: '8px',
        marginTop: '32px',
        height: 'calc(100vh - 165px)',
        position: 'relative',
        backgroundColor: '#fffff'
    }
}));

const MarksBreakdown = () => {
    const classes = useStyles();
    const [open, setOpen] = React.useState(false);
    const [selectedRow, setSelectedRow] = React.useState<any>(null);
    const [selection, setSelection] = useState<any>([])
    const [tableData, setTableData] = React.useState<any[]>([]);
    const [activeSemester, setActiveSemester] = useState(1);
    const [semestersData, setSemestersData] = useState<any[]>([]);

    useEffect(() => {
        getMarksBreakdown().then((res) => {
            const response = Array.isArray(res?.data) ? res?.data : [];
            const sortedResponse = [...response].sort(
                (a: any, b: any) => Number(a?.Semester ?? 0) - Number(b?.Semester ?? 0)
            );

            const semesters = sortedResponse.map((item: any, index: number) => {
                const courses = (item?.Courses || []).map((course: any, idx: number) => {
                    const creditsEarnedValue = Number(course?.CreditsEarned);
                    const hasCreditsEarned = Number.isFinite(creditsEarnedValue);
                    return {
                        id: course?.id || `${item?.Semester || index + 1}-${idx}`,
                        courseName: course?.CourseName,
                        courseCode: course?.CourseCode,
                        credits: Number(course?.Credits) || 0,
                        creditsEarned: hasCreditsEarned ? creditsEarnedValue : "-",
                        creditsEarnedValue: hasCreditsEarned ? creditsEarnedValue : 0,
                        compositeScore: course?.CompositeScore,
                        grade: course?.LetterGrade,
                        CourseConnectionId: course?.CourseConnectionId
                    };
                });

                const creditsEarned = courses.reduce((sum: number, c: any) => sum + (c.creditsEarnedValue || 0), 0);
                const creditsTotal = courses.reduce((sum: number, c: any) => sum + (c.credits || 0), 0);

                return {
                    Semester: item?.Semester ?? index + 1,
                    Gpa: typeof item?.GPA === "number" ? item?.GPA : Number(item?.GPA) || null,
                    creditsEarned,
                    creditsTotal,
                    Courses: courses
                };
            });

            setSemestersData(semesters);
            setSelection(semesters);

            const initialSemester = semesters[0]?.Semester ?? 1;
            setActiveSemester(initialSemester);
            setTableData(semesters[0]?.Courses || []);
        }).catch((err) => {
            console.error("Error in fetching marks breakdown:", err);
        })
    }, []);

    const handleOpenModal = (row: any) => {
        console.log("bdsbsjkd", row);

        setSelectedRow(row);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedRow(null);
    };

    const handleChange = (event: SelectChangeEvent<number>) => {
        setActiveSemester(Number(event.target.value));
    };

    useEffect(() => {
        const current = semestersData.find((sem) => sem.Semester === activeSemester);
        if (current) {
            setTableData(current.Courses || []);
        }
    }, [activeSemester, semestersData]);

    const currentSemesterData = useMemo(
        () => semestersData.find((sem) => sem.Semester === activeSemester),
        [activeSemester, semestersData]
    );


    const columns = [
        { field: "courseName", headerName: "Course name" },
        { field: "courseCode", headerName: "Course code" },
        { field: "credits", headerName: "Credits" },
        { field: "creditsEarned", headerName: "Credits earned" },
        { field: "compositeScore", headerName: "Composite score" },
        {
            field: "grade",
            headerName: "Grade",
            renderCell: ({ value }: any) => (
                <span
                    style={{
                        fontWeight: 600,
                        color: value === "F" ? "red" : "#000",
                    }}
                >
                    {value}
                </span>
            ),
        },
        {
            field: "marksBreakdown",
            headerName: "Marks breakdown",
            renderCell: ({ row }: any) => (
                <IconButton size="small" onClick={() => handleOpenModal(row)}>
                    <VisibilityIcon />
                </IconButton>
            ),
        },
    ];
    return (
        <Box>
            <Header title='Marks breakdown' />
            <Box component={Paper} className={classes.container}>
                <Box className="d-flex justify-content-between align-items-center ">
                    <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 500, color: "#333" }}
                    >
                        Marks breakdown
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, alignItems: 'center' }}>
                        <Box
                            sx={{
                                border: "1px solid #B2B3B7",
                                background: '#F6F8FB',
                                borderRadius: "8px",
                                px: 1,
                                py: 1,
                                fontSize: 14,
                            }}
                        >
                            Credits:{" "}
                            <Typography component="span" fontWeight={600}>
                                {currentSemesterData?.creditsEarned ?? 0}/{currentSemesterData?.creditsTotal ?? "--"}
                            </Typography> | GPA:{" "}
                            <Typography component="span" fontWeight={600}>
                                {currentSemesterData?.Gpa ?? "--"}
                            </Typography>
                        </Box>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            value={activeSemester}
                            onChange={handleChange}
                            displayEmpty
                            fullWidth
                            inputProps={{ 'aria-label': 'Without label' }}
                            sx={{
                                height: '42px',
                                width: '300px', '& .MuiSelect-select': {
                                    padding: '10px 14px',
                                },
                            }}
                        >
                            {selection.map((sem: any) => (
                                <MenuItem key={sem.Semester} value={sem.Semester}>
                                    Semester {sem.Semester}
                                    {activeSemester === sem.Semester && (
                                        <Chip
                                            label="Active semester"
                                            sx={{
                                                ml: 1,
                                                borderRadius: "16px",
                                                background: "#C7B7D1",
                                                color: "#373A45",
                                                fontSize: '12px',
                                                padding: '4px !important'
                                            }}
                                        />
                                    )}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                </Box>
                <Box className="mt-4">
                    <KenTable
                        columns={columns}
                        rows={tableData}
                        showCheckbox={false} // ✅ No checkboxes here
                    />
                </Box>
                <Dialog PaperProps={{
                    sx: {
                        padding: '0px',
                        width: "80%",   // 👈 custom width
                        maxWidth: "none" // 👈 remove default maxWidth restriction
                    },
                }} open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        <Box className="d-flex justify-content-between align-items-center">
                            <Typography sx={{ fontSize: '20px', fontWeight: 700 }}> Marks Breakdown</Typography>
                            <Box ><CloseIcon onClick={() => handleClose()} /></Box>
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <NetworkAnalysis data={selectedRow} />
                    </DialogContent>
                </Dialog>
            </Box>
        </Box>
    )
}

export default MarksBreakdown
